import * as Sentry from '@sentry/browser';

export function interpolateUri(
  template: string,
  params?: Record<string, string | number | boolean | Array<string | number>>
): string {
  if (!params) return template;

  return template.replace(/:([a-zA-Z]+)/g, (_, key) => {
    if (params[key] === undefined) {
      throw new Error(`Missing parameter: ${key}`);
    }
    return encodeURIComponent(params[key].toString());
  });
}

/**
 * Handle non-OK API responses with Sentry context and detailed error messages.
 * Returns undefined for 429 (rate-limit) responses, throws for all others.
 */
export async function handleApiError<T>(
  response: Response,
  method: string,
  url: string,
): Promise<T> {
  let responseBody: unknown = null;
  try {
    const responseText = await response.text();
    try {
      responseBody = JSON.parse(responseText);
    } catch {
      responseBody = responseText || null;
    }
  } catch {
    responseBody = null;
  }

  Sentry.setContext('apiErrorResponse', {
    status: response.status,
    method,
    url,
    body:
      typeof responseBody === 'string'
        ? responseBody.slice(0, 2000)
        : JSON.stringify(responseBody).slice(0, 2000),
  });

  if (response.status === 429) {
    console.warn(`Warning: Too many requests on ${method} ${url}.`);
    return undefined as T;
  }

  const bodyRecord =
    responseBody && typeof responseBody === 'object'
      ? (responseBody as Record<string, unknown>)
      : undefined;
  const bodyMessage = bodyRecord?.['message'] ?? bodyRecord?.['Message'];
  const errorMessage = bodyMessage || response.statusText || 'Unknown error';
  throw new Error(`Error: ${response.status}: ${errorMessage}`);
}
